import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { Empty, ErrorState, Loading } from '../ui/State';

type Organisation = {
  id: string;
  legal_name: string;
  trading_name?: string;
  company_number?: string;
  status: string;
  contact_count: number;
  role_count: number;
  membership_count: number;
  document_count: number;
  communication_count: number;
};
type Group = { key: string; confidence: 'Strong' | 'Review'; reason: string; organisations: Organisation[] };

export function OrganisationDuplicateReview() {
  const [groups, setGroups] = useState<Group[]>();
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState('');
  const [error, setError] = useState<unknown>();
  const load = () => api<Group[]>('/api/organisations/duplicate-candidates').then(setGroups).catch(setError);
  useEffect(() => { void load(); }, []);

  async function merge(group: Group) {
    const survivor = selected[group.key];
    const duplicates = group.organisations.filter((item) => item.id !== survivor);
    if (!survivor || !duplicates.length) return;
    setSaving(group.key);
    setError(undefined);
    try {
      const body = JSON.stringify({
        survivingOrganisationId: survivor,
        mergedOrganisationIds: duplicates.map((duplicate) => duplicate.id),
        reason: 'Authorised user confirmed these records represent the same legal organisation.',
      });
      await api('/api/organisations/merge-preview', { method: 'POST', body });
      await api('/api/organisations/merge', { method: 'POST', body });
      await load();
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving('');
    }
  }

  if (error && !groups) return <ErrorState error={error} />;
  if (!groups) return <Loading />;
  return <section className="panel">
    <div className="section-heading"><div><h2>Possible duplicate organisations</h2><p>Review strong and suggested matches. Nothing is merged automatically.</p></div><span>{groups.length}</span></div>
    {!groups.length ? <Empty>No possible organisation duplicates need review.</Empty> : <div className="duplicate-groups">{groups.map((group) => <article key={group.key}>
      <header><span className={`badge ${group.confidence === 'Review' ? 'warning' : ''}`}>{group.confidence === 'Strong' ? 'Strong match' : 'Needs review'}</span><p>{group.reason}</p></header>
      <fieldset><legend>Choose the master record to keep</legend>{group.organisations.map((item) => <label key={item.id} className="duplicate-option">
        <input type="radio" name={`survivor-${group.key}`} checked={selected[group.key] === item.id} onChange={() => setSelected({ ...selected, [group.key]: item.id })} />
        <span><strong>{item.legal_name}</strong>{item.trading_name && <small>Trading as {item.trading_name}</small>}<small>{item.company_number ? `Company ${item.company_number}` : 'No company number'} · {item.status}</small><small>{item.contact_count} contact details · {item.role_count} roles · {item.membership_count} members · {item.document_count} documents · {item.communication_count} communications</small><Link to={`/records/organisation/${item.id}`}>View record</Link></span>
      </label>)}</fieldset>
      <label className="inline-check"><input type="checkbox" checked={confirmed[group.key] ?? false} onChange={(event) => setConfirmed({ ...confirmed, [group.key]: event.target.checked })} /><span>I have confirmed these records represent the same legal organisation.</span></label>
      <button disabled={saving === group.key || !selected[group.key] || !confirmed[group.key]} onClick={() => void merge(group)}>{saving === group.key ? 'Merging…' : 'Merge duplicate records into selected master'}</button>
    </article>)}</div>}
    {error ? <ErrorState error={error} /> : null}
  </section>;
}
